import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type QueryDocumentSnapshot,
  type Unsubscribe
} from "firebase/firestore";
import { db } from "../firebase/client";

const COMMUNITY_COLLECTION = "travelerQuizCommunity";
const COMMUNITY_REVIEW_FIELD = "communityReviews";
const COMMUNITY_VOTE_FIELD = "communityReviewVotes";
const COMMUNITY_READ_LIMIT = 500;

export type WorldReviewEntityType = "country" | "city" | "place" | "poi" | "restaurant" | "experience";
export type WorldReviewVote = "helpful" | "notHelpful";

export interface WorldCommunityReview {
  id: string;
  authorUid: string;
  authorName: string;
  authorUsername: string;
  entityType: WorldReviewEntityType;
  entityKey: string;
  countryCode: string;
  cityName?: string;
  entityName?: string;
  address?: string;
  googlePlaceId?: string;
  rating: number;
  text: string;
  helpfulVoters: string[];
  notHelpfulVoters: string[];
  helpfulCount: number;
  notHelpfulCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CommunityReviewEntityInsight {
  entityType: WorldReviewEntityType;
  entityKey: string;
  name: string;
  cityName?: string;
  countryCode?: string;
  reviews: number;
  averageRating: number;
  helpfulVotes: number;
  notHelpfulVotes: number;
  communityScore: number;
}

export interface CommunityReviewKnowledge {
  source: string;
  sampledUsers: number;
  totalReviews: number;
  topEntities: CommunityReviewEntityInsight[];
  reviewSamples: Array<{
    entityType: WorldReviewEntityType;
    name: string;
    cityName?: string;
    countryCode?: string;
    rating: number;
    text: string;
    helpfulVotes: number;
    notHelpfulVotes: number;
    updatedAt: string;
  }>;
}

type CommunityDoc = Record<string, unknown>;
type StoredReview = Record<string, unknown>;
type StoredVote = { reviewId?: unknown; vote?: unknown; updatedAt?: unknown };

function safePart(value: string): string {
  return String(value || "").trim().toLocaleLowerCase().replace(/[^a-z0-9_-]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 72) || "item";
}

function stableHash(value: string): string {
  let hash = 2166136261;
  const source = String(value || "");
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function reviewStorageKey(entityKey: string): string {
  return `${safePart(entityKey).slice(0, 48)}_${stableHash(entityKey)}`;
}

function voteStorageKey(reviewId: string): string {
  return `vote_${stableHash(reviewId)}_${safePart(reviewId).slice(-24)}`;
}

export function countryReviewEntityKey(countryCode: string): string {
  return `country:${String(countryCode || "").trim().toUpperCase()}`;
}

export function cityReviewEntityKey(countryCode: string, cityName: string): string {
  return `city:${String(countryCode || "").trim().toUpperCase()}:${safePart(cityName)}`;
}

export function communityPlaceReviewEntityKey(input: {
  entityType: Exclude<WorldReviewEntityType, "country" | "city">;
  googlePlaceId?: string;
  name: string;
  city?: string;
  address?: string;
}): string {
  const placeId = String(input.googlePlaceId || "").trim();
  if (placeId) return `${input.entityType}:google:${safePart(placeId)}`;
  return `${input.entityType}:manual:${safePart(input.name)}:${safePart(input.city || "")}:${safePart(input.address || "")}`;
}

function reviewDocumentId(authorUid: string, entityKey: string): string {
  return `${safePart(authorUid)}__${reviewStorageKey(entityKey)}`;
}

function normalizeReview(id: string, raw: StoredReview, fallbackAuthorUid = ""): WorldCommunityReview {
  const helpfulVoters = Array.isArray(raw.helpfulVoters) ? raw.helpfulVoters.map(String) : [];
  const notHelpfulVoters = Array.isArray(raw.notHelpfulVoters) ? raw.notHelpfulVoters.map(String) : [];
  return {
    id: String(raw.id || id),
    authorUid: String(raw.authorUid || fallbackAuthorUid),
    authorName: String(raw.authorName || ""),
    authorUsername: String(raw.authorUsername || ""),
    entityType: (["country", "city", "place", "poi", "restaurant", "experience"].includes(String(raw.entityType)) ? String(raw.entityType) : "country") as WorldReviewEntityType,
    entityKey: String(raw.entityKey || ""),
    countryCode: String(raw.countryCode || "").toUpperCase(),
    cityName: raw.cityName ? String(raw.cityName) : undefined,
    entityName: raw.entityName ? String(raw.entityName) : undefined,
    address: raw.address ? String(raw.address) : undefined,
    googlePlaceId: raw.googlePlaceId ? String(raw.googlePlaceId) : undefined,
    rating: Math.max(0, Math.min(5, Number(raw.rating || 0))),
    text: String(raw.text || ""),
    helpfulVoters,
    notHelpfulVoters,
    helpfulCount: helpfulVoters.length,
    notHelpfulCount: notHelpfulVoters.length,
    createdAt: String(raw.createdAt || raw.updatedAt || ""),
    updatedAt: String(raw.updatedAt || raw.createdAt || "")
  };
}

function objectField(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function buildReviewsFromCommunityDocs(docs: QueryDocumentSnapshot[]): WorldCommunityReview[] {
  const reviews = new Map<string, WorldCommunityReview>();
  const votes: Array<{ voterUid: string; reviewId: string; vote: WorldReviewVote }> = [];

  docs.forEach((entry) => {
    const data = entry.data() as CommunityDoc;
    const storedReviews = objectField(data[COMMUNITY_REVIEW_FIELD]);
    Object.entries(storedReviews).forEach(([storageKey, raw]) => {
      if (!raw || typeof raw !== "object") return;
      const normalized = normalizeReview(`${safePart(entry.id)}__${storageKey}`, raw as StoredReview, entry.id);
      if (!normalized.entityKey || !normalized.text.trim()) return;
      normalized.helpfulVoters = [];
      normalized.notHelpfulVoters = [];
      normalized.helpfulCount = 0;
      normalized.notHelpfulCount = 0;
      reviews.set(normalized.id, normalized);
    });

    const storedVotes = objectField(data[COMMUNITY_VOTE_FIELD]);
    Object.values(storedVotes).forEach((raw) => {
      if (!raw || typeof raw !== "object") return;
      const vote = raw as StoredVote;
      const reviewId = String(vote.reviewId || "").trim();
      const value = String(vote.vote || "");
      if (!reviewId || (value !== "helpful" && value !== "notHelpful")) return;
      votes.push({ voterUid: entry.id, reviewId, vote: value as WorldReviewVote });
    });
  });

  votes.forEach(({ voterUid, reviewId, vote }) => {
    const review = reviews.get(reviewId);
    if (!review || review.authorUid === voterUid) return;
    if (vote === "helpful") review.helpfulVoters.push(voterUid);
    else review.notHelpfulVoters.push(voterUid);
  });

  reviews.forEach((review) => {
    review.helpfulCount = review.helpfulVoters.length;
    review.notHelpfulCount = review.notHelpfulVoters.length;
  });

  return Array.from(reviews.values()).sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""));
}

function communityQuery() {
  return query(collection(db, COMMUNITY_COLLECTION), limit(COMMUNITY_READ_LIMIT));
}

function mergeReviews(primary: WorldCommunityReview[], legacy: WorldCommunityReview[]): WorldCommunityReview[] {
  const merged = new Map<string, WorldCommunityReview>();
  legacy.forEach((review) => merged.set(`${review.authorUid}|${review.entityKey}`, review));
  primary.forEach((review) => merged.set(`${review.authorUid}|${review.entityKey}`, review));
  return Array.from(merged.values()).sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""));
}

function normalizeLegacySnapshot(docs: QueryDocumentSnapshot[]): WorldCommunityReview[] {
  return docs.map((entry) => normalizeReview(entry.id, entry.data() as StoredReview)).filter((item) => Boolean(item.entityKey && item.text.trim()));
}

export function watchWorldCommunityReviews(entityKey: string, onChange: (reviews: WorldCommunityReview[]) => void): Unsubscribe {
  const target = String(entityKey || "").trim();
  if (!target) {
    onChange([]);
    return () => undefined;
  }

  let communityReviews: WorldCommunityReview[] = [];
  let legacyReviews: WorldCommunityReview[] = [];
  const emit = () => onChange(mergeReviews(communityReviews, legacyReviews).filter((item) => item.entityKey === target));

  const communityUnsubscribe = onSnapshot(communityQuery(), (snapshot) => {
    communityReviews = buildReviewsFromCommunityDocs(snapshot.docs);
    emit();
  }, () => {
    communityReviews = [];
    emit();
  });

  const legacyRequest = query(collection(db, "worldReviews"), where("entityKey", "==", target));
  const legacyUnsubscribe = onSnapshot(legacyRequest, (snapshot) => {
    legacyReviews = normalizeLegacySnapshot(snapshot.docs);
    emit();
  }, () => {
    legacyReviews = [];
    emit();
  });

  return () => {
    communityUnsubscribe();
    legacyUnsubscribe();
  };
}

export async function loadWorldReviewsByAuthor(authorUid: string): Promise<WorldCommunityReview[]> {
  const uid = String(authorUid || "").trim();
  if (!uid) return [];
  const [communityReviews, legacyReviews] = await Promise.all([
    getDocs(communityQuery()).then((snapshot) => buildReviewsFromCommunityDocs(snapshot.docs)).catch(() => [] as WorldCommunityReview[]),
    getDocs(query(collection(db, "worldReviews"), where("authorUid", "==", uid))).then((snapshot) => normalizeLegacySnapshot(snapshot.docs)).catch(() => [] as WorldCommunityReview[])
  ]);
  return mergeReviews(communityReviews, legacyReviews).filter((item) => item.authorUid === uid);
}

async function writeReview(input: {
  authorUid: string;
  authorName: string;
  authorUsername: string;
  entityType: WorldReviewEntityType;
  entityKey: string;
  countryCode?: string;
  cityName?: string;
  entityName?: string;
  address?: string;
  googlePlaceId?: string;
  rating?: number;
  text: string;
}): Promise<void> {
  const uid = String(input.authorUid || "").trim();
  if (!uid) throw new Error("REVIEW_USER_REQUIRED");
  const entityKey = String(input.entityKey || "").trim();
  if (!entityKey) throw new Error("REVIEW_ENTITY_REQUIRED");
  const storageKey = reviewStorageKey(entityKey);
  const target = doc(db, COMMUNITY_COLLECTION, uid);
  const text = String(input.text || "").trim();

  if (!text) {
    try {
      await updateDoc(target, { [`${COMMUNITY_REVIEW_FIELD}.${storageKey}`]: deleteField() });
    } catch {
      // Deleting a review that was never synced is already the desired end state.
    }
    notifyReviewUpdate(uid);
    return;
  }

  const now = new Date().toISOString();
  let createdAt = now;
  try {
    const existingSnapshot = await getDoc(target);
    const existingReviews = existingSnapshot.exists() ? objectField((existingSnapshot.data() as CommunityDoc)[COMMUNITY_REVIEW_FIELD]) : {};
    const existingReview = existingReviews[storageKey];
    if (existingReview && typeof existingReview === "object") {
      createdAt = String((existingReview as StoredReview).createdAt || now);
    }
  } catch {
    // createdAt can safely start now; it is only presentation metadata.
  }

  const reviewId = reviewDocumentId(uid, entityKey);
  const review: StoredReview = {
    id: reviewId,
    authorUid: uid,
    authorName: String(input.authorName || "").trim(),
    authorUsername: String(input.authorUsername || "").trim(),
    entityType: input.entityType,
    entityKey,
    countryCode: String(input.countryCode || "").trim().toUpperCase(),
    cityName: String(input.cityName || "").trim(),
    entityName: String(input.entityName || "").trim(),
    address: String(input.address || "").trim(),
    googlePlaceId: String(input.googlePlaceId || "").trim(),
    rating: Math.max(0, Math.min(5, Number(input.rating || 0))),
    text,
    createdAt,
    updatedAt: now
  };

  await setDoc(target, {
    communityUpdatedAt: now,
    communityServerUpdatedAt: serverTimestamp()
  }, { merge: true });
  await updateDoc(target, { [`${COMMUNITY_REVIEW_FIELD}.${storageKey}`]: review });
  notifyReviewUpdate(uid);
}

function notifyReviewUpdate(uid: string): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("tripai:world-review-updated", { detail: { uid } }));
  window.dispatchEvent(new CustomEvent("tripai:gamification-source-updated", { detail: { uid } }));
}

export async function publishWorldReview(input: {
  authorUid: string;
  authorName: string;
  authorUsername: string;
  entityType: "country" | "city";
  countryCode: string;
  cityName?: string;
  rating?: number;
  text: string;
}): Promise<void> {
  const countryCode = String(input.countryCode || "").trim().toUpperCase();
  const cityName = String(input.cityName || "").trim();
  const entityKey = input.entityType === "city" ? cityReviewEntityKey(countryCode, cityName) : countryReviewEntityKey(countryCode);
  await writeReview({
    ...input,
    countryCode,
    cityName,
    entityKey,
    entityName: input.entityType === "city" ? cityName : countryCode
  });
}

export async function publishPlaceCommunityReview(input: {
  authorUid: string;
  authorName: string;
  authorUsername: string;
  entityType: Exclude<WorldReviewEntityType, "country" | "city">;
  googlePlaceId?: string;
  name: string;
  city?: string;
  address?: string;
  rating?: number;
  text: string;
}): Promise<void> {
  const entityKey = communityPlaceReviewEntityKey(input);
  await writeReview({
    authorUid: input.authorUid,
    authorName: input.authorName,
    authorUsername: input.authorUsername,
    entityType: input.entityType,
    entityKey,
    entityName: input.name,
    cityName: input.city,
    address: input.address,
    googlePlaceId: input.googlePlaceId,
    rating: input.rating,
    text: input.text
  });
}

export async function voteWorldReview(reviewId: string, voterUid: string, vote: WorldReviewVote): Promise<void> {
  const uid = String(voterUid || "").trim();
  if (!uid) throw new Error("VOTER_REQUIRED");
  const targetReviewId = String(reviewId || "").trim();
  if (!targetReviewId) throw new Error("REVIEW_NOT_FOUND");

  const communitySnapshot = await getDocs(communityQuery());
  const targetReview = buildReviewsFromCommunityDocs(communitySnapshot.docs).find((item) => item.id === targetReviewId);
  if (!targetReview) throw new Error("REVIEW_NOT_FOUND");
  if (targetReview.authorUid === uid) throw new Error("OWN_REVIEW_VOTE_NOT_ALLOWED");

  const voterTarget = doc(db, COMMUNITY_COLLECTION, uid);
  const key = voteStorageKey(targetReviewId);
  let currentVote: WorldReviewVote | "" = "";
  try {
    const voterSnapshot = await getDoc(voterTarget);
    const storedVotes = voterSnapshot.exists() ? objectField((voterSnapshot.data() as CommunityDoc)[COMMUNITY_VOTE_FIELD]) : {};
    const stored = storedVotes[key];
    if (stored && typeof stored === "object") {
      const value = String((stored as StoredVote).vote || "");
      if (value === "helpful" || value === "notHelpful") currentVote = value;
    }
  } catch {
    currentVote = "";
  }

  if (currentVote === vote) {
    try {
      await updateDoc(voterTarget, { [`${COMMUNITY_VOTE_FIELD}.${key}`]: deleteField() });
    } catch {
      // Already absent is equivalent to toggling the vote off.
    }
  } else {
    const now = new Date().toISOString();
    await setDoc(voterTarget, {
      communityUpdatedAt: now,
      communityServerUpdatedAt: serverTimestamp()
    }, { merge: true });
    await updateDoc(voterTarget, {
      [`${COMMUNITY_VOTE_FIELD}.${key}`]: {
        reviewId: targetReviewId,
        vote,
        updatedAt: now
      }
    });
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("tripai:gamification-source-updated", { detail: { uid: targetReview.authorUid } }));
  }
}

function insightName(review: WorldCommunityReview): string {
  return review.entityName || review.cityName || review.countryCode || review.entityKey;
}

function focusWords(value: string): string[] {
  const normalized = String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const ignored = new Set(["della", "delle", "degli", "dello", "dell", "alla", "alle", "agli", "allo", "with", "from", "about", "this", "that", "trip", "viaggio", "travel", "para", "desde", "sobre", "este", "esta"]);
  return Array.from(new Set(normalized.split(/[^a-z0-9]+/).filter((word) => word.length >= 4 && !ignored.has(word)))).slice(0, 12);
}

function reviewFocusScore(review: WorldCommunityReview, words: string[]): number {
  if (!words.length) return 0;
  const haystack = [review.entityName, review.cityName, review.countryCode, review.address, review.text]
    .filter(Boolean)
    .join(" ")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  return words.reduce((score, word) => score + (haystack.includes(word) ? 1 : 0), 0);
}

export async function loadCommunityReviewInsights(maxUsers = 250, maxEntities = 24, maxSamples = 18, focus = ""): Promise<CommunityReviewKnowledge> {
  const empty: CommunityReviewKnowledge = {
    source: "Mundely public community reviews; subjective traveller opinions, not objective facts",
    sampledUsers: 0,
    totalReviews: 0,
    topEntities: [],
    reviewSamples: []
  };
  try {
    const [snapshot, legacySnapshot] = await Promise.all([
      getDocs(query(collection(db, COMMUNITY_COLLECTION), limit(Math.max(1, Math.min(COMMUNITY_READ_LIMIT, maxUsers))))),
      getDocs(query(collection(db, "worldReviews"), limit(Math.max(1, Math.min(COMMUNITY_READ_LIMIT, maxUsers))))).catch(() => null)
    ]);
    const communityReviews = buildReviewsFromCommunityDocs(snapshot.docs);
    const legacyReviews = legacySnapshot ? normalizeLegacySnapshot(legacySnapshot.docs) : [];
    const reviews = mergeReviews(communityReviews, legacyReviews).filter((item) => item.text.trim());
    const words = focusWords(focus);
    const groups = new Map<string, WorldCommunityReview[]>();
    reviews.forEach((review) => {
      const bucket = groups.get(review.entityKey) || [];
      bucket.push(review);
      groups.set(review.entityKey, bucket);
    });

    const topEntities = Array.from(groups.values()).map((items): CommunityReviewEntityInsight => {
      const sample = items[0];
      const rated = items.filter((item) => item.rating > 0);
      const averageRating = rated.length ? rated.reduce((sum, item) => sum + item.rating, 0) / rated.length : 0;
      const helpfulVotes = items.reduce((sum, item) => sum + item.helpfulCount, 0);
      const notHelpfulVotes = items.reduce((sum, item) => sum + item.notHelpfulCount, 0);
      return {
        entityType: sample.entityType,
        entityKey: sample.entityKey,
        name: insightName(sample),
        cityName: sample.cityName,
        countryCode: sample.countryCode || undefined,
        reviews: items.length,
        averageRating: Number(averageRating.toFixed(2)),
        helpfulVotes,
        notHelpfulVotes,
        communityScore: helpfulVotes - notHelpfulVotes
      };
    }).sort((left, right) => {
      const leftFocus = words.reduce((score, word) => score + ([left.name, left.cityName, left.countryCode].filter(Boolean).join(" ").toLowerCase().includes(word) ? 1 : 0), 0);
      const rightFocus = words.reduce((score, word) => score + ([right.name, right.cityName, right.countryCode].filter(Boolean).join(" ").toLowerCase().includes(word) ? 1 : 0), 0);
      return (
        rightFocus - leftFocus ||
        right.communityScore - left.communityScore ||
        right.reviews - left.reviews ||
        right.averageRating - left.averageRating ||
        left.name.localeCompare(right.name)
      );
    }).slice(0, Math.max(1, maxEntities));

    const reviewSamples = reviews.slice().sort((left, right) => (
      reviewFocusScore(right, words) - reviewFocusScore(left, words) ||
      (right.helpfulCount - right.notHelpfulCount) - (left.helpfulCount - left.notHelpfulCount) ||
      right.helpfulCount - left.helpfulCount ||
      (right.updatedAt || "").localeCompare(left.updatedAt || "")
    )).slice(0, Math.max(1, maxSamples)).map((review) => ({
      entityType: review.entityType,
      name: insightName(review),
      cityName: review.cityName,
      countryCode: review.countryCode || undefined,
      rating: review.rating,
      text: review.text.slice(0, 700),
      helpfulVotes: review.helpfulCount,
      notHelpfulVotes: review.notHelpfulCount,
      updatedAt: review.updatedAt
    }));

    return {
      source: empty.source,
      sampledUsers: snapshot.size,
      totalReviews: reviews.length,
      topEntities,
      reviewSamples
    };
  } catch {
    return empty;
  }
}
